import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import AdminSubscriptionManager from '@/components/admin/AdminSubscriptionManager';
import AdminDiscountManager from '@/components/admin/AdminDiscountManager';
import AdminHubManager from '@/components/admin/AdminHubManager';
import AdminJobRunsPanel from '@/components/admin/AdminJobRunsPanel';
import AdminOnboardingFunnel from '@/components/admin/AdminOnboardingFunnel';
import AdminSecurityLog from '@/components/admin/AdminSecurityLog';
import AdminOwnerDigestCard from '@/components/admin/AdminOwnerDigestCard';
import {
  Shield, Users, Settings, BarChart3, Search, Ban, CheckCircle,
  Trash2, UserPlus, Crown, Code, Calculator, ArrowLeft, Loader2,
  ToggleLeft, ToggleRight, RefreshCw, AlertTriangle, Eye, DollarSign,
  Edit, TrendingUp, CreditCard, Receipt, PiggyBank, Gift, Globe, Clock,
  ShieldAlert
} from 'lucide-react';
import { getErrorMessage } from '../utils/errorHandler';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_ICONS = {
  owner: Crown,
  admin: Shield,
  developer: Code,
  accounting: Calculator,
  moderator: Eye
};

const ROLE_COLORS = {
  owner: 'bg-amber-500 text-black',
  admin: 'bg-blue-500 text-white',
  developer: 'bg-purple-500 text-white',
  accounting: 'bg-green-500 text-white',
  moderator: 'bg-orange-500 text-white'
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { t } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminRole, setAdminRole] = useState(null);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [features, setFeatures] = useState([]);
  const [roles, setRoles] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Dialog states
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [userToBan, setUserToBan] = useState(null);
  const [banReason, setBanReason] = useState('');
  const [banPermanent, setBanPermanent] = useState(false);
  const [banDays, setBanDays] = useState(7);
  
  const [addAdminDialogOpen, setAddAdminDialogOpen] = useState(false);
  const [newAdminUserId, setNewAdminUserId] = useState('');
  const [newAdminRole, setNewAdminRole] = useState('admin');
  
  const [userDetailOpen, setUserDetailOpen] = useState(false);
  const [userDetail, setUserDetail] = useState(null);
  
  // Edit admin dialog
  const [editAdminDialogOpen, setEditAdminDialogOpen] = useState(false);
  const [adminToEdit, setAdminToEdit] = useState(null);
  const [editAdminRole, setEditAdminRole] = useState('');
  
  // Financials data
  const [financials, setFinancials] = useState(null);
  const [loadingFinancials, setLoadingFinancials] = useState(false);

  // Check admin status
  const checkAdminStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
      setIsAdmin(true);
      
      // Get admin role
      const adminsRes = await axios.get(`${API}/admin/admins`);
      const currentAdmin = adminsRes.data.admins.find(a => a.user_id === user?.user_id);
      setAdminRole(currentAdmin?.role);
      setRoles(adminsRes.data.roles);
    } catch (error) {
      if (error.response?.status === 403) {
        setIsAdmin(false);
      }
    } finally {
      setLoading(false);
    }
  }, [user?.user_id]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        params: { search: searchQuery || undefined, limit: 100 }
      });
      setUsers(response.data.users);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, [searchQuery]);

  const fetchAdmins = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/admins`);
      setAdmins(response.data.admins);
      setRoles(response.data.roles);
    } catch (error) {
      console.error('Failed to fetch admins:', error);
    }
  }, []);

  const fetchFeatures = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/features`);
      setFeatures(response.data.features);
    } catch (error) {
      console.error('Failed to fetch features:', error);
    }
  }, []);

  const fetchFinancials = useCallback(async () => {
    setLoadingFinancials(true);
    try {
      const response = await axios.get(`${API}/admin/financials`);
      setFinancials(response.data);
    } catch (error) {
      console.error('Failed to fetch financials:', error);
      // Financials might not be accessible for non-accounting roles
    } finally {
      setLoadingFinancials(false);
    }
  }, []);

  useEffect(() => {
    checkAdminStatus();
  }, [checkAdminStatus]);

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
      fetchAdmins();
      fetchFeatures();
      // Financials only for owner or accounting role
      if (adminRole === 'owner' || adminRole === 'accounting') {
        fetchFinancials();
      }
    }
  }, [isAdmin, adminRole, fetchUsers, fetchAdmins, fetchFeatures, fetchFinancials]);

  // Handlers
  const handleBanUser = async () => {
    if (!userToBan || !banReason.trim()) return;
    
    try {
      await axios.post(`${API}/admin/users/${userToBan.user_id}/ban`, {
        reason: banReason,
        is_permanent: banPermanent,
        expires_in_days: banPermanent ? null : banDays
      });
      toast.success(`User ${userToBan.name} has been banned`);
      setBanDialogOpen(false);
      setUserToBan(null);
      setBanReason('');
      fetchUsers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to ban user'));
    }
  };

  const handleUnbanUser = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/unban`);
      toast.success('User has been unbanned');
      fetchUsers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to unban user'));
    }
  };

  const handleAddAdmin = async () => {
    if (!newAdminUserId.trim()) return;
    
    try {
      await axios.post(`${API}/admin/admins`, {
        user_id: newAdminUserId,
        role: newAdminRole
      });
      toast.success('Admin created successfully');
      setAddAdminDialogOpen(false);
      setNewAdminUserId('');
      fetchAdmins();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create admin'));
    }
  };

  const handleRemoveAdmin = async (adminId) => {
    try {
      await axios.delete(`${API}/admin/admins/${adminId}`);
      toast.success('Admin removed');
      fetchAdmins();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to remove admin'));
    }
  };

  const handleEditAdmin = async () => {
    if (!adminToEdit || !editAdminRole) return;
    
    try {
      await axios.put(`${API}/admin/admins/${adminToEdit.admin_id}`, {
        role: editAdminRole,
        permissions: []
      });
      toast.success(`Role updated to ${editAdminRole}`);
      setEditAdminDialogOpen(false);
      setAdminToEdit(null);
      fetchAdmins();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update admin'));
    }
  };

  const handleMakeAdmin = async (userId, role = 'admin') => {
    try {
      await axios.post(`${API}/admin/admins`, {
        user_id: userId,
        role: role
      });
      toast.success('Admin created successfully');
      fetchAdmins();
      fetchUsers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create admin'));
    }
  };

  const handleToggleFeature = async (featureId, enabled) => {
    try {
      await axios.put(`${API}/admin/features/${featureId}`, { enabled: !enabled });
      toast.success(`Feature ${enabled ? 'disabled' : 'enabled'}`);
      fetchFeatures();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update feature'));
    }
  };

  const handleViewUser = async (userId) => {
    try {
      const response = await axios.get(`${API}/admin/users/${userId}`);
      setUserDetail(response.data);
      setUserDetailOpen(true);
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };

  const initializeOwner = async () => {
    try {
      await axios.post(`${API}/admin/init-owner`);
      toast.success('You are now the owner!');
      checkAdminStatus();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to initialize owner'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Shield className="w-16 h-16 mx-auto text-amber-500 mb-4" />
            <CardTitle>{t('admin_access_required') || 'Admin Access Required'}</CardTitle>
            <CardDescription>
              {t('no_admin_permission') || 'You dont have permission to access the admin dashboard.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('back_to_dashboard') || 'Back to Dashboard'}
            </Button>
            <Button 
              className="w-full bg-amber-500 hover:bg-amber-600 text-black"
              onClick={initializeOwner}
            >
              <Crown className="w-4 h-4 mr-2" />
              {t('become_owner') || 'Become Owner (First Setup)'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const RoleIcon = ROLE_ICONS[adminRole] || Shield;

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-red-500 flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-xl">{t('admin_dashboard')}</h1>
                <div className="flex items-center gap-2">
                  <Badge className={ROLE_COLORS[adminRole]}>
                    <RoleIcon className="w-3 h-3 mr-1" />
                    {adminRole?.toUpperCase()}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
          
          <Button variant="outline" onClick={() => { fetchUsers(); fetchAdmins(); fetchFeatures(); checkAdminStatus(); }}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Total Users</p>
                    <p className="text-3xl font-bold text-white">{stats.total_users}</p>
                  </div>
                  <Users className="w-10 h-10 text-blue-500" />
                </div>
                <p className="text-xs text-green-400 mt-2">+{stats.new_users_this_week} this week</p>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Banned Users</p>
                    <p className="text-3xl font-bold text-red-400">{stats.banned_users}</p>
                  </div>
                  <Ban className="w-10 h-10 text-red-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Projects</p>
                    <p className="text-3xl font-bold text-white">{stats.total_projects}</p>
                  </div>
                  <BarChart3 className="w-10 h-10 text-amber-500" />
                </div>
                <p className="text-xs text-green-400 mt-2">+{stats.new_projects_this_week} this week</p>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Admins</p>
                    <p className="text-3xl font-bold text-white">{stats.total_admins}</p>
                  </div>
                  <Shield className="w-10 h-10 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Tabs */}
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="bg-slate-900 border border-slate-800 flex-wrap h-auto justify-start w-full sm:w-auto">
            <TabsTrigger value="users" className="gap-2 data-[state=active]:bg-amber-500 data-[state=active]:text-black">
              <Users className="w-4 h-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="admins" className="gap-2 data-[state=active]:bg-amber-500 data-[state=active]:text-black">
              <Shield className="w-4 h-4" />
              Admins
            </TabsTrigger>
            <TabsTrigger value="features" className="gap-2 data-[state=active]:bg-amber-500 data-[state=active]:text-black">
              <Settings className="w-4 h-4" />
              Features
            </TabsTrigger>
            {(adminRole === 'owner' || adminRole === 'accounting') && (
              <TabsTrigger value="subscriptions" className="gap-2 data-[state=active]:bg-purple-500 data-[state=active]:text-black">
                <Gift className="w-4 h-4" />
                Subscriptions
              </TabsTrigger>
            )}
            {(adminRole === 'owner' || adminRole === 'accounting') && (
              <TabsTrigger value="discounts" className="gap-2 data-[state=active]:bg-pink-500 data-[state=active]:text-black">
                <Receipt className="w-4 h-4" />
                Discounts
              </TabsTrigger>
            )}
            {(adminRole === 'owner' || adminRole === 'accounting') && (
              <TabsTrigger value="financials" className="gap-2 data-[state=active]:bg-green-500 data-[state=active]:text-black">
                <DollarSign className="w-4 h-4" />
                Financials
              </TabsTrigger>
            )}
            {(adminRole === 'owner' || adminRole === 'admin') && (
              <TabsTrigger value="hub" className="gap-2 data-[state=active]:bg-cyan-500 data-[state=active]:text-black" data-testid="hub-tab">
                <Globe className="w-4 h-4" />
                Hub
              </TabsTrigger>
            )}
            {(adminRole === 'owner' || adminRole === 'admin' || adminRole === 'developer') && (
              <TabsTrigger value="jobs" className="gap-2 data-[state=active]:bg-amber-500 data-[state=active]:text-black" data-testid="jobs-tab">
                <Clock className="w-4 h-4" />
                Jobs
              </TabsTrigger>
            )}
            {(adminRole === 'owner' || adminRole === 'admin' || adminRole === 'developer') && (
              <TabsTrigger value="security" className="gap-2 data-[state=active]:bg-red-500 data-[state=active]:text-black" data-testid="security-tab">
                <Shield className="w-4 h-4" />
                Security
              </TabsTrigger>
            )}
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>User Management</CardTitle>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search users..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 bg-slate-800 border-slate-700 w-64"
                      />
                    </div>
                    <Button variant="outline" onClick={fetchUsers}>
                      <Search className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-2">
                    {users.map((u) => (
                      <div
                        key={u.user_id}
                        className={`flex items-center justify-between p-4 rounded-lg ${
                          u.is_banned ? 'bg-red-900/20 border border-red-800' : 'bg-slate-800/50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarImage src={u.picture} />
                            <AvatarFallback>{u.name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{u.name}</p>
                              {u.is_banned && (
                                <Badge variant="destructive" className="text-xs">BANNED</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-400">{u.email}</p>
                            <p className="text-xs text-slate-500">
                              {u.user_type} {u.user_subcategory && `- ${u.user_subcategory}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewUser(u.user_id)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {adminRole === 'owner' && !admins.some(a => a.user_id === u.user_id) && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-purple-400 border-purple-400"
                              onClick={() => { setNewAdminUserId(u.user_id); setAddAdminDialogOpen(true); }}
                              data-testid={`make-admin-${u.user_id}`}
                            >
                              <Shield className="w-4 h-4 mr-1" />
                              Admin
                            </Button>
                          )}
                          {u.is_banned ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-green-400 border-green-400"
                              onClick={() => handleUnbanUser(u.user_id)}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Unban
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-400 border-red-400"
                              onClick={() => { setUserToBan(u); setBanDialogOpen(true); }}
                            >
                              <Ban className="w-4 h-4 mr-1" />
                              Ban
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Admins Tab */}
          <TabsContent value="admins">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Admin Management</CardTitle>
                  {adminRole === 'owner' && (
                    <Button onClick={() => setAddAdminDialogOpen(true)} className="bg-amber-500 hover:bg-amber-600 text-black">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add Admin
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {admins.map((admin) => {
                    const RIcon = ROLE_ICONS[admin.role] || Shield;
                    return (
                      <div
                        key={admin.admin_id}
                        className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50"
                      >
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarImage src={admin.user?.picture} />
                            <AvatarFallback>{admin.user?.name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{admin.user?.name}</p>
                              <Badge className={ROLE_COLORS[admin.role]}>
                                <RIcon className="w-3 h-3 mr-1" />
                                {admin.role}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-400">{admin.user?.email}</p>
                            <p className="text-xs text-slate-500">
                              {admin.role_info?.description}
                            </p>
                          </div>
                        </div>
                        {adminRole === 'owner' && admin.role !== 'owner' && (
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-blue-400"
                              onClick={() => { setAdminToEdit(admin); setEditAdminRole(admin.role); setEditAdminDialogOpen(true); }}
                              data-testid={`edit-admin-${admin.admin_id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-400"
                              onClick={() => handleRemoveAdmin(admin.admin_id)}
                              data-testid={`remove-admin-${admin.admin_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Role Descriptions */}
                <div className="mt-8 pt-6 border-t border-slate-700">
                  <h3 className="text-lg font-semibold mb-4">Available Roles</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(roles).map(([key, role]) => {
                      const RIcon = ROLE_ICONS[key] || Shield;
                      return (
                        <div key={key} className="p-4 rounded-lg bg-slate-800/30 border border-slate-700">
                          <div className="flex items-center gap-2 mb-2">
                            <RIcon className={`w-5 h-5 ${key === 'owner' ? 'text-amber-500' : 'text-slate-400'}`} />
                            <span className="font-medium">{role.name}</span>
                          </div>
                          <p className="text-sm text-slate-400">{role.description}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Features Tab */}
          <TabsContent value="features">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle>Feature Flags</CardTitle>
                <CardDescription>Enable or disable app features</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {features.map((feature) => (
                    <div
                      key={feature.feature_id}
                      className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50"
                    >
                      <div className="flex items-center gap-4">
                        {feature.enabled ? (
                          <ToggleRight className="w-6 h-6 text-green-500" />
                        ) : (
                          <ToggleLeft className="w-6 h-6 text-slate-500" />
                        )}
                        <div>
                          <p className="font-medium">{feature.name}</p>
                          <p className="text-sm text-slate-400">{feature.description}</p>
                          <code className="text-xs text-amber-400">{feature.key}</code>
                        </div>
                      </div>
                      <Switch
                        checked={feature.enabled}
                        onCheckedChange={() => handleToggleFeature(feature.feature_id, feature.enabled)}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscriptions Tab - Only for owner and accounting roles */}
          {(adminRole === 'owner' || adminRole === 'accounting') && (
            <TabsContent value="subscriptions">
              <AdminSubscriptionManager token={token} />
            </TabsContent>
          )}

          {/* Discounts Tab - Only for owner and accounting roles */}
          {(adminRole === 'owner' || adminRole === 'accounting') && (
            <TabsContent value="discounts">
              <AdminDiscountManager />
            </TabsContent>
          )}

          {/* Financials Tab - Only for owner and accounting roles */}
          {(adminRole === 'owner' || adminRole === 'accounting') && (
            <TabsContent value="financials">
              {adminRole === 'owner' && (
                <div className="mb-6">
                  <AdminOwnerDigestCard token={token} />
                </div>
              )}
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-green-500" />
                        Financial Overview
                      </CardTitle>
                      <CardDescription>View financial metrics and analytics</CardDescription>
                    </div>
                    <Button variant="outline" onClick={fetchFinancials} disabled={loadingFinancials}>
                      <RefreshCw className={`w-4 h-4 mr-2 ${loadingFinancials ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingFinancials ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-green-500" />
                    </div>
                  ) : financials ? (
                    <div className="space-y-6">
                      {/* Revenue Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card className="bg-gradient-to-br from-green-900/50 to-green-800/30 border-green-700">
                          <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm text-green-300">Total Revenue</p>
                                <p className="text-3xl font-bold text-green-400">${financials.total_revenue?.toLocaleString() || '0'}</p>
                              </div>
                              <TrendingUp className="w-10 h-10 text-green-500" />
                            </div>
                            <p className="text-xs text-green-400/70 mt-2">
                              +${financials.revenue_this_month?.toLocaleString() || '0'} this month
                            </p>
                          </CardContent>
                        </Card>
                        
                        <Card className="bg-gradient-to-br from-blue-900/50 to-blue-800/30 border-blue-700">
                          <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm text-blue-300">Active Subscriptions</p>
                                <p className="text-3xl font-bold text-blue-400">{financials.active_subscriptions || 0}</p>
                              </div>
                              <CreditCard className="w-10 h-10 text-blue-500" />
                            </div>
                            <p className="text-xs text-blue-400/70 mt-2">
                              {financials.subscription_growth || 0}% growth
                            </p>
                          </CardContent>
                        </Card>
                        
                        <Card className="bg-gradient-to-br from-purple-900/50 to-purple-800/30 border-purple-700">
                          <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm text-purple-300">Avg. Revenue/User</p>
                                <p className="text-3xl font-bold text-purple-400">${financials.arpu?.toFixed(2) || '0.00'}</p>
                              </div>
                              <PiggyBank className="w-10 h-10 text-purple-500" />
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Recent Transactions */}
                      <div>
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                          <Receipt className="w-5 h-5" />
                          Recent Transactions
                        </h3>
                        {financials.recent_transactions?.length > 0 ? (
                          <div className="space-y-2">
                            {financials.recent_transactions.map((tx, idx) => (
                              <div key={idx} className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50">
                                <div className="flex items-center gap-3">
                                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                    tx.type === 'subscription' ? 'bg-blue-500/20' : 'bg-green-500/20'
                                  }`}>
                                    {tx.type === 'subscription' ? (
                                      <CreditCard className="w-5 h-5 text-blue-400" />
                                    ) : (
                                      <DollarSign className="w-5 h-5 text-green-400" />
                                    )}
                                  </div>
                                  <div>
                                    <p className="font-medium">{tx.description || tx.type}</p>
                                    <p className="text-sm text-slate-400">{tx.user_email}</p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="font-bold text-green-400">+${tx.amount?.toFixed(2)}</p>
                                  <p className="text-xs text-slate-500">{new Date(tx.date).toLocaleDateString()}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-slate-400">
                            <Receipt className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>No transactions yet</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-400">
                      <DollarSign className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p className="text-lg">Financial data not available</p>
                      <p className="text-sm">This section will populate once payment integration is enabled</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Hub Tab - Central Admin Hub */}
          {(adminRole === 'owner' || adminRole === 'admin') && (
            <TabsContent value="hub">
              <AdminHubManager />
            </TabsContent>
          )}

          {/* Jobs Tab - scheduled background jobs */}
          {(adminRole === 'owner' || adminRole === 'admin' || adminRole === 'developer') && (
            <TabsContent value="jobs" className="space-y-6">
              <AdminJobRunsPanel token={token} />
              <AdminOnboardingFunnel token={token} />
            </TabsContent>
          )}

          {/* Security Tab - audit log + session revoke */}
          {(adminRole === 'owner' || adminRole === 'admin' || adminRole === 'developer') && (
            <TabsContent value="security" className="space-y-6">
              <AdminSecurityLog token={token} />
            </TabsContent>
          )}
        </Tabs>
      </main>

      {/* Ban User Dialog */}
      <Dialog open={banDialogOpen} onOpenChange={setBanDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Ban User
            </DialogTitle>
            <DialogDescription>
              Ban {userToBan?.name} ({userToBan?.email})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Reason for ban</Label>
              <Textarea
                placeholder="Enter reason..."
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Permanent ban</Label>
              <Switch checked={banPermanent} onCheckedChange={setBanPermanent} />
            </div>
            {!banPermanent && (
              <div className="space-y-2">
                <Label>Ban duration (days)</Label>
                <Input
                  type="number"
                  value={banDays}
                  onChange={(e) => setBanDays(parseInt(e.target.value))}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBanDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleBanUser}>Ban User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Admin Dialog */}
      <Dialog open={addAdminDialogOpen} onOpenChange={setAddAdminDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle>Add Admin</DialogTitle>
            <DialogDescription>Grant admin privileges to a user</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>User ID</Label>
              <Input
                placeholder="user_xxxxxxxxxxxx"
                value={newAdminUserId}
                onChange={(e) => setNewAdminUserId(e.target.value)}
                className="bg-slate-800 border-slate-700"
              />
              {newAdminUserId && (
                <p className="text-xs text-slate-400">
                  {users.find(u => u.user_id === newAdminUserId)?.name || 'User not found'}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={newAdminRole} onValueChange={setNewAdminRole}>
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(roles).filter(([k]) => k !== 'owner').map(([key, role]) => (
                    <SelectItem key={key} value={key}>{role.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddAdminDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddAdmin} className="bg-amber-500 hover:bg-amber-600 text-black">Add Admin</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Admin Role Dialog */}
      <Dialog open={editAdminDialogOpen} onOpenChange={setEditAdminDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-500" />
              Edit Admin Role
            </DialogTitle>
            <DialogDescription>
              Change role for {adminToEdit?.user?.name} ({adminToEdit?.user?.email})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-3 p-3 bg-slate-800 rounded-lg">
              <Avatar>
                <AvatarImage src={adminToEdit?.user?.picture} />
                <AvatarFallback>{adminToEdit?.user?.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium">{adminToEdit?.user?.name}</p>
                <p className="text-sm text-slate-400">Current role: <Badge className={ROLE_COLORS[adminToEdit?.role]}>{adminToEdit?.role}</Badge></p>
              </div>
            </div>
            <div className="space-y-2">
              <Label>New Role</Label>
              <Select value={editAdminRole} onValueChange={setEditAdminRole}>
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(roles).filter(([k]) => k !== 'owner').map(([key, role]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        {(() => {
                          const RIcon = ROLE_ICONS[key] || Shield;
                          return <RIcon className="w-4 h-4" />;
                        })()}
                        {role.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {editAdminRole && roles[editAdminRole] && (
                <p className="text-xs text-slate-400 mt-1">{roles[editAdminRole].description}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditAdminDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleEditAdmin} className="bg-blue-500 hover:bg-blue-600 text-white">Update Role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Detail Dialog */}
      <Dialog open={userDetailOpen} onOpenChange={setUserDetailOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          {userDetail && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4">
                <Avatar className="w-16 h-16">
                  <AvatarImage src={userDetail.picture} />
                  <AvatarFallback className="text-2xl">{userDetail.name?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-lg font-semibold">{userDetail.name}</h3>
                  <p className="text-slate-400">{userDetail.email}</p>
                  {userDetail.is_banned && <Badge variant="destructive">BANNED</Badge>}
                  {userDetail.is_admin && <Badge className={ROLE_COLORS[userDetail.admin_role]}>{userDetail.admin_role}</Badge>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-800 rounded-lg">
                  <p className="text-xs text-slate-400">User Type</p>
                  <p className="font-medium capitalize">{userDetail.user_type || 'Not set'}</p>
                </div>
                <div className="p-3 bg-slate-800 rounded-lg">
                  <p className="text-xs text-slate-400">Subcategory</p>
                  <p className="font-medium capitalize">{userDetail.user_subcategory || 'None'}</p>
                </div>
                <div className="p-3 bg-slate-800 rounded-lg">
                  <p className="text-xs text-slate-400">Projects</p>
                  <p className="font-medium">{userDetail.projects_count}</p>
                </div>
                <div className="p-3 bg-slate-800 rounded-lg">
                  <p className="text-xs text-slate-400">Communities</p>
                  <p className="font-medium">{userDetail.communities_count}</p>
                </div>
              </div>
              <div className="p-3 bg-slate-800 rounded-lg">
                <p className="text-xs text-slate-400">User ID</p>
                <code className="text-amber-400 text-sm">{userDetail.user_id}</code>
              </div>
              {userDetail.ban_info && (
                <div className="p-3 bg-red-900/20 border border-red-800 rounded-lg">
                  <p className="text-xs text-red-400">Ban Reason</p>
                  <p className="font-medium">{userDetail.ban_info.reason}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
